import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  getBorgoDellaSettimana,
  getComuneLight,
  getFrazione,
  placesMap,
} from "../api";
import { safeApiCall } from "../utils/apiResilience";
import { tryGeolocationComune, saveLastComune } from "../utils/searchContext";
import { getPositionWithFallback } from "../utils/getPositionWithFallback";
import {
  classifyVisitorPosition,
  internationalWelcomeMessage,
  resolveVisitorCity,
} from "../utils/geoInternational";
import BorgoOnboardingBanner from "../components/BorgoOnboardingBanner";
import { useAuth } from "../AuthContext";
import {
  activeGeofenceZones,
  distanceMeters,
  isInsideGeofence,
} from "../utils/prossimita/proximityEngines";
import geofenceData from "../data/geofenceZones.json";
import { HOME_BORGHI_CONSIGLIATI } from "../data/tePilotBorghi";
import { HERO_BY_SLUG, heroForSlug, HERO_FALLBACK_IMG } from "../utils/heroImages";
import { comuneAdminPhoto, comuneHero } from "../utils/regionImages";
import { risolviFotoMontorioUfficiale } from "../data/territorio";
import { resolveMediaUrl } from "../utils/mediaUrl";
import { monumentPlainBlurb } from "../components/comune/StoriaMarkdownArticle";

const CiceroneContext = createContext(null);

const POI_RADIUS_M = 450;
const GEO_POI_PAD = 0.008;

/** Borghi Ischia — esclusi dai consigliati home (rete Abruzzo priorità). */
const ISCHIA_BORGO_SLUGS = new Set([
  "ischia",
  "casamicciola-terme",
  "lacco-ameno",
  "forio",
  "serrara-fontana",
  "barano-d-ischia",
]);

const BORGHI_CONSIGLIATI_SWAP = {
  ischia: { slug: "mosciano-sant-angelo", name: "Mosciano Sant'Angelo" },
  "serrara-fontana": { slug: "pietracamela", name: "Pietracamela" },
};

function normalizeBorgoConsigliato(b) {
  if (!b?.slug) return null;
  const swap = BORGHI_CONSIGLIATI_SWAP[b.slug];
  const slug = swap?.slug || b.slug;
  const name = swap?.name || b.name || slug;
  const fromApi =
    comuneAdminPhoto(b.comune) ||
    (b.comune ? comuneHero({ ...b.comune, slug: b.comune?.slug || slug }) : "");
  const hero = resolveMediaUrl(
    fromApi ||
      risolviFotoMontorioUfficiale(slug) ||
      risolviFotoMontorioUfficiale(b.comuneSlug) ||
      heroForSlug(slug) ||
      heroForSlug(b.comuneSlug) ||
      HERO_BY_SLUG[slug] ||
      b.hero ||
      HERO_FALLBACK_IMG,
  );
  return {
    ...b,
    slug,
    name,
    hero,
    href: b.href || `/comune/${b.comuneSlug || slug}`,
  };
}

async function verifyComuneInCensus(slug) {
  if (!slug) return false;
  const data = await safeApiCall(() => getComuneLight(slug), {
    timeoutMs: 4000,
    fallback: null,
  });
  return Boolean(data?.slug || data?.name);
}

async function loadRecommendedBorghi() {
  const borgo = await safeApiCall(() => getBorgoDellaSettimana(), {
    timeoutMs: 5000,
    fallback: null,
  });
  const featuredSlug = (borgo?.comune?.slug || "").trim().toLowerCase();

  const comuni = await Promise.all(
    HOME_BORGHI_CONSIGLIATI.map((b) => {
      // Frazioni `comune__frazione`: il light dei comuni non le risolve —
      // leggo il doc frazione così le foto admin del borgo arrivano alle card.
      if ((b.slug || "").includes("__")) {
        return safeApiCall(() => getFrazione(b.slug), { timeoutMs: 4000, fallback: null });
      }
      return safeApiCall(() => getComuneLight(b.comuneSlug || b.slug), { timeoutMs: 4000, fallback: null });
    }),
  );

  return HOME_BORGHI_CONSIGLIATI.map((b, i) =>
    normalizeBorgoConsigliato({
      ...b,
      comune: comuni[i],
      featured: b.slug === featuredSlug || b.comuneSlug === featuredSlug,
    }),
  ).filter((b) => b?.slug && !ISCHIA_BORGO_SLUGS.has(b.slug));
}

function nearestMonumentFromItems(items, lat, lng) {
  let best = null;
  let bestD = Infinity;
  for (const it of items || []) {
    if (it.kind !== "monument" || typeof it.lat !== "number" || typeof it.lng !== "number") continue;
    const d = distanceMeters(lat, lng, it.lat, it.lng);
    if (d < bestD && d <= POI_RADIUS_M) {
      bestD = d;
      best = { ...it, distanceM: Math.round(d) };
    }
  }
  return best;
}

function nearestGeofencePoi(position) {
  if (!position) return null;
  const zones = activeGeofenceZones(geofenceData.zones, geofenceData.defaults?.radiusM ?? 500);
  let best = null;
  let bestD = Infinity;
  for (const zone of zones) {
    if (!isInsideGeofence(position, zone)) continue;
    const d = distanceMeters(position.lat, position.lng, zone.lat, zone.lng);
    if (d < bestD) {
      bestD = d;
      best = {
        id: zone.id,
        kind: "geofence",
        name: zone.name,
        description: `Sei nell'area Cicerone di ${zone.name}. Scopri cosa fare oggi nel borgo.`,
        slug: zone.comuneSlug,
        href: zone.href || (zone.comuneSlug ? `/comune/${zone.comuneSlug}` : "/mappa-del-mondo"),
        lat: zone.lat,
        lng: zone.lng,
        distanceM: Math.round(d),
      };
    }
  }
  return best;
}

async function detectNearbyPoi(position) {
  if (!position?.lat || !position?.lng) return null;

  const geofenceHit = nearestGeofencePoi(position);
  const { lat, lng } = position;
  const pad = GEO_POI_PAD;

  const mapData = await safeApiCall(
    () =>
      placesMap({
        min_lat: lat - pad,
        min_lng: lng - pad,
        max_lat: lat + pad,
        max_lng: lng + pad,
        kinds: "monument",
        limit: 20,
      }),
    { timeoutMs: 5000, fallback: { items: [] } },
  );

  const monument = nearestMonumentFromItems(mapData?.items, lat, lng);
  if (monument) {
    return {
      id: monument.id || monument.slug,
      kind: "monument",
      name: monument.name || "Monumento",
      description:
        monumentPlainBlurb(
          monument.description || monument.short_description,
          280,
        ) ||
        "Un luogo di memoria storica da scoprire con la voce del Cicerone digitale.",
      image: monument.image || monument.photo || null,
      href: monument.id ? `/monumento/${monument.id}` : monument.slug ? `/monumento/${monument.slug}` : null,
      lat: monument.lat,
      lng: monument.lng,
      distanceM: monument.distanceM,
    };
  }

  return geofenceHit;
}

export function CiceroneProvider({ children }) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [geoStatus, setGeoStatus] = useState("idle");
  const [comune, setComune] = useState(null);
  const [inCensus, setInCensus] = useState(false);
  const [position, setPosition] = useState(null);
  const [recommendedBorghi, setRecommendedBorghi] = useState([]);
  const [nearbyPoi, setNearbyPoi] = useState(null);
  const [geoFallbackNotice, setGeoFallbackNotice] = useState(false);
  const [isInternational, setIsInternational] = useState(false);
  const [visitorCity, setVisitorCity] = useState(null);
  const [internationalWelcome, setInternationalWelcome] = useState(null);
  const [networkScope, setNetworkScope] = useState("local"); // local | all
  const [localDistanceFilter, setLocalDistanceFilter] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    const applyFallbackAudience = (pos, reason = "fallback") => {
      if (!mountedRef.current) return;
      setPosition({
        lat: pos?.lat ?? 42.7264,
        lng: pos?.lng ?? 13.6861,
        source: "fallback",
        reason,
      });
      setIsInternational(false);
      setNetworkScope("all");
      setLocalDistanceFilter(false);
      setGeoStatus("ready");
    };

    (async () => {
      setGeoStatus("detecting");

      try {
        const pos = await getPositionWithFallback();
        if (!mountedRef.current) return;

        if (pos.usedFallback) {
          applyFallbackAudience(pos, pos.reason || "fallback");
        } else {
          let audience;
          try {
            audience = classifyVisitorPosition(pos.lat, pos.lng);
          } catch (err) {
            console.warn("Cicerone classify:", err);
            applyFallbackAudience(pos, "classify-error");
            audience = null;
          }

          if (audience?.international) {
            setPosition({ lat: pos.lat, lng: pos.lng, source: "geo-international" });
            setIsInternational(true);
            setNetworkScope("all");
            setLocalDistanceFilter(false);
            setComune(null);
            setInCensus(false);
            try {
              setInternationalWelcome(internationalWelcomeMessage());
            } catch {
              setInternationalWelcome(null);
            }
            setGeoStatus("ready");

            resolveVisitorCity(pos.lat, pos.lng)
              .then((city) => {
                if (mountedRef.current && city) setVisitorCity(city);
              })
              .catch(() => {});
          } else if (audience) {
            setPosition({ lat: pos.lat, lng: pos.lng, source: "geo" });
            setIsInternational(false);
            setNetworkScope(audience.networkScope || "local");
            setLocalDistanceFilter(Boolean(audience.localDistanceFilter));
            setGeoStatus("ready");

            try {
              const geo = await safeApiCall(() => tryGeolocationComune(), {
                timeoutMs: 3500,
                fallback: null,
              });
              if (!mountedRef.current) return;

              if (geo?.slug) {
                const censusOk = await verifyComuneInCensus(geo.slug);
                if (!mountedRef.current) return;
                if (censusOk) saveLastComune(geo.slug, geo.name);
                setComune({ slug: geo.slug, name: geo.name, source: geo.source || "geo" });
                setInCensus(censusOk);
              }
            } catch (err) {
              console.warn("Cicerone comune geo:", err);
            }
          }
        }

        if (mountedRef.current) setGeoStatus((s) => (s === "detecting" ? "ready" : s));

        try {
          const borghi = await loadRecommendedBorghi();
          if (mountedRef.current) setRecommendedBorghi(Array.isArray(borghi) ? borghi : []);
        } catch (err) {
          console.warn("Cicerone borghi:", err);
          if (mountedRef.current) setRecommendedBorghi([]);
        }
      } catch (err) {
        console.warn("Cicerone bootstrap:", err);
        if (!mountedRef.current) return;
        try {
          const pos = await getPositionWithFallback({ timeout: 800 });
          applyFallbackAudience(pos, "bootstrap-error");
        } catch {
          applyFallbackAudience(null, "bootstrap-error");
        }
        if (mountedRef.current) setRecommendedBorghi([]);
      }
    })();

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!position) return undefined;
    if (position.source === "fallback" || position.source === "geo-international") return undefined;
    if (!localDistanceFilter) return undefined;
    let cancelled = false;

    detectNearbyPoi(position)
      .then((poi) => {
        if (!cancelled && poi) setNearbyPoi(poi);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [position, localDistanceFilter]);

  const dismissNearbyPoi = () => setNearbyPoi(null);
  const dismissGeoFallbackNotice = () => setGeoFallbackNotice(false);
  const dismissInternationalWelcome = () => setInternationalWelcome(null);

  return (
    <CiceroneContext.Provider
      value={{
        geoStatus,
        comune,
        inCensus,
        position,
        recommendedBorghi,
        nearbyPoi,
        dismissNearbyPoi,
        geoFallbackNotice,
        dismissGeoFallbackNotice,
        isInternational,
        visitorCity,
        internationalWelcome,
        dismissInternationalWelcome,
        networkScope,
        localDistanceFilter,
        showWelcome: Boolean(comune && inCensus && !isInternational),
        showInternationalWelcome: Boolean(isInternational && internationalWelcome),
        showRecommended: geoStatus === "ready" && recommendedBorghi.length > 0,
      }}
    >
      {children}
      <BorgoOnboardingBanner user={user} pathname={pathname} />
    </CiceroneContext.Provider>
  );
}

export function useCicerone() {
  const ctx = useContext(CiceroneContext);
  if (!ctx) {
    return {
      geoStatus: "idle",
      comune: null,
      inCensus: false,
      position: null,
      recommendedBorghi: [],
      nearbyPoi: null,
      dismissNearbyPoi: () => {},
      geoFallbackNotice: false,
      dismissGeoFallbackNotice: () => {},
      isInternational: false,
      visitorCity: null,
      internationalWelcome: null,
      dismissInternationalWelcome: () => {},
      networkScope: "local",
      localDistanceFilter: true,
      showWelcome: false,
      showInternationalWelcome: false,
      showRecommended: false,
    };
  }
  return ctx;
}
